'use client'
import { useEffect, useRef } from 'react'
import { useUser } from 'auth'
import { useDispatch } from 'shared-redux'
import { baseSplitApi } from 'shared-redux/state/store-utils'
import { type DataResponse, useGetCachedRequest } from 'api/utils/next'

export const ANONYMOUS_SCOPE = 'anon'

interface CustomerScope {
  /** Cache scope of the current session: the customer id, or `anon`. */
  scope: string
  /** `false` while `/api/auth/user` is still in flight and the scope is unknown. */
  isResolved: boolean
}

/**
 * Product responses depend on who is asking (B2B price lists, buyability), so any
 * client cache holding them has to be keyed by the session on top of the request
 * url. Without it, logging in or out reuses the previous session's response until
 * a full page reload throws the cache away.
 */
export const useCustomerScope = (): CustomerScope => {
  const { user, isLoading } = useUser()

  return {
    scope: user?.customer?.id ?? ANONYMOUS_SCOPE,
    isResolved: !isLoading
  }
}

/**
 * `useGetCachedRequest` for endpoints whose response depends on the session.
 *
 * The scope only takes part in the cache key — it is never sent to the server,
 * which resolves the customer from the session cookie itself. The request waits
 * for the scope to be known so a logged-in customer never gets the anonymous
 * response rendered first.
 */
export const useCustomerScopedRequest = <T>(
  url: string | null,
  key?: string
): DataResponse<T | null> => {
  const { scope, isResolved } = useCustomerScope()
  const result = useGetCachedRequest<T>(isResolved ? url : null, key, scope)

  return isResolved ? result : { ...result, isLoading: url !== null }
}

/**
 * Drops the RTK Query cache whenever the session changes.
 *
 * RTK Query keys its entries by request arguments only, so `get-products`
 * (load more) results would otherwise survive a login with the previous
 * session's prices. Call it from hooks that read customer-dependent data
 * through `baseSplitApi`.
 */
export const useResetApiCacheOnCustomerChange = () => {
  const { scope } = useCustomerScope()
  const dispatch = useDispatch()
  const previousScope = useRef(scope)

  useEffect(() => {
    if (previousScope.current === scope) return

    previousScope.current = scope
    dispatch(baseSplitApi.util.resetApiState())
  }, [scope, dispatch])
}
